import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';

import { filter } from 'rxjs/operators';

import { CoreMenuItem } from 'app/back-office/shared/models/core';
import { CoreMenuService } from 'app/back-office/layout/core-menu/core-menu.service';
import { FeatherIconDirective } from 'app/back-office/shared/directives/feather-icon/feather-icon.directive';
import { CoreMenuVerticalItemComponent } from 'app/back-office/layout/core-menu/vertical/item/item.component';

@Component({
    selector: '[core-menu-vertical-collapsible]',
    imports: [NgClass, NgTemplateOutlet, RouterModule, TranslateModule, FeatherIconDirective, CoreMenuVerticalItemComponent],
    templateUrl: './collapsible.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    host: { '[class.open]': 'isOpen()' }
})
export class CoreMenuVerticalCollapsibleComponent implements OnInit {
  item = input.required<CoreMenuItem>();

  isOpen = signal(false);

  private _router = inject(Router);
  private _coreMenuService = inject(CoreMenuService);
  private _destroyRef = inject(DestroyRef);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Listen for router events and expand
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        // Confirm if the urlAfterRedirects can be found in one of the children of this item
        if (this.confirmUrlInChildren(this.item(), event.urlAfterRedirects)) {
          this.expand();
        } else {
          this.collapse();
        }
      });

    // Listen for collapsing of any menu item
    this._coreMenuService.onItemCollapsed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(clickedItem => {
      if (clickedItem && clickedItem.children) {
        // Check if the clicked item is one of the children of this item
        if (this.confirmItemInChildren(this.item(), clickedItem)) {
          return;
        }

        // Check if the url can be found in one of the children of this item
        if (this.confirmUrlInChildren(this.item(), this._router.url)) {
          return;
        }

        // If the clicked item is not this item, collapse...
        if (this.item() !== clickedItem) {
          this.collapse();
        }
      }
    });

    // Check if the url can be found in one of the children of this item
    // Required for onInit case (i.e switching theme customizer menu layout)
    if (this.confirmUrlInChildren(this.item(), this._router.url)) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle collapse
   *
   * @param e
   */
  toggleOpen(e: Event): void {
    e.preventDefault();

    this.isOpen.update(isOpen => !isOpen);
    this._changeDetectorRef.markForCheck();

    // Menu collapse toggled...
    this._coreMenuService.onItemCollapsed.next(this.item());
    this._coreMenuService.onItemCollapseToggled.next(undefined);
  }

  /**
   * Expand the collapsible menu
   */
  expand(): void {
    if (this.isOpen()) {
      return;
    }

    this.isOpen.set(true);

    // Il binding host [class.open] è valutato nella view del padre (core-menu,
    // OnPush): senza markForCheck l'apertura da router-event non viene applicata
    this._changeDetectorRef.markForCheck();

    this._coreMenuService.onItemCollapseToggled.next(undefined);
  }

  /**
   * Collapse the collapsible menu
   */
  collapse(): void {
    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);

    this._changeDetectorRef.markForCheck();

    this._coreMenuService.onItemCollapseToggled.next(undefined);
  }

  /**
   * Confirms if the provided url can be found in one of the given parent's children
   *
   * @param parent
   * @param url
   * @returns {boolean}
   */
  confirmUrlInChildren(parent: CoreMenuItem, url: string): boolean {
    const children = parent.children;

    // Return false if parent don't have any children
    if (!children) {
      return false;
    }

    // Loop all the children
    for (const child of children) {
      // If children has child (Sub to sub item url)
      if (child.children) {
        // Call function again with child
        if (this.confirmUrlInChildren(child, url)) {
          return true;
        }
      }

      // If child.url is same as provided url
      if (child.url === url || url.includes(child.url)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if the provided parent has the provided item in one of its children
   *
   * @param parent
   * @param item
   * @returns {boolean}
   */
  confirmItemInChildren(parent: CoreMenuItem, item: CoreMenuItem): boolean {
    const children = parent.children;

    // Return false if parent don't have any children
    if (!children) {
      return false;
    }

    // Return true parent has the provided item in one of its children
    if (children.indexOf(item) > -1) {
      return true;
    }

    for (const child of children) {
      if (child.children) {
        // Call function again with child (for sub to sub item)
        if (this.confirmItemInChildren(child, item)) {
          return true;
        }
      }
    }

    return false;
  }
}
